import React, { useState, useRef, useCallback } from 'react';
import { useAuth } from '../../../auth';
import { addAlgorithmTime } from '../../../utils/firebase';
import './Timer.css';
import StatsContainer from '../../statistics/containers/StatsContainer';

const Timer = ({
  algorithmId,
  mode,
  onComplete,
  onSolveSaved,
  children
}) => {
  const { user } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const startTimeRef = useRef(null);
  const [pressStartTime, setPressStartTime] = useState(null);
  const [savedEventId, setSavedEventId] = useState(null);
  const [pendingSaves, setPendingSaves] = useState(0);
  const [saveFailure, setSaveFailure] = useState(null);
  const nextSolveSeq = useRef(0);
  const latestSavedSeq = useRef(-1);
  const failedSeq = useRef(-1);
  const PRESS_DURATION = 550;

  React.useEffect(() => {
    setElapsedTime(0);
  }, [algorithmId]);

  const handleStart = useCallback(() => {
    setIsRunning(true);
    startTimeRef.current = performance.now();
  }, []);

  const handleStop = useCallback(() => {
    const endTime = performance.now();
    const timeElapsed = endTime - startTimeRef.current;
    setElapsedTime(timeElapsed);
    setIsRunning(false);

    if (!(user && algorithmId && mode)) {
      return;
    }

    const seq = nextSolveSeq.current;
    nextSolveSeq.current += 1;
    const solve = { algorithmId, mode, time: timeElapsed };

    const noteSaveFailure = () => {
      if (seq < latestSavedSeq.current) {
        return;
      }
      failedSeq.current = seq;
      setSaveFailure({ algorithmId: solve.algorithmId, time: solve.time });
      setSavedEventId(`unsaved-${seq}`);
    };

    setPendingSaves((count) => count + 1);
    addAlgorithmTime(solve.algorithmId, solve.time, solve.mode)
      .then((eventId) => {
        if (!eventId) {
          noteSaveFailure();
          return;
        }
        setSavedEventId(eventId);
        if (seq >= failedSeq.current) {
          setSaveFailure(null);
        }
        if (seq >= latestSavedSeq.current) {
          latestSavedSeq.current = seq;
          if (onSolveSaved) {
            onSolveSaved({ eventId, ...solve });
          }
        }
      })
      .catch((error) => {
        console.error('Error saving solve:', error);
        noteSaveFailure();
      })
      .finally(() => {
        setPendingSaves((count) => count - 1);
      });

    if (onComplete) {
      onComplete(timeElapsed);
    }
  }, [algorithmId, mode, user, onComplete, onSolveSaved]);

  const handlePressStart = useCallback((e) => {
    e.preventDefault();
    
    if (e.type === 'keydown' && e.code === 'Space') {
      e.preventDefault();
    }
    
    if (!isRunning && !pressStartTime) {
      setPressStartTime(Date.now());
    }
  }, [isRunning, pressStartTime]);

  const handlePressEnd = useCallback((e) => {
    e.preventDefault();
    
    if (!pressStartTime) return;

    const pressDuration = Date.now() - pressStartTime;
    setPressStartTime(null);

    if (pressDuration >= PRESS_DURATION) {
      if (!isRunning) {
        handleStart();
      } else {
        handleStop();
      }
    }
  }, [pressStartTime, isRunning, handleStart, handleStop]);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (isRunning) {
        // Stop timer on any keypress when running
        e.preventDefault();
        handleStop();
      } else if (e.code === 'Space') {
        // Only space bar starts the timer
        e.preventDefault();
        handlePressStart(e);
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space' && !isRunning) {
        e.preventDefault();
        handlePressEnd(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isRunning, handleStop, handlePressStart, handlePressEnd]);

  React.useEffect(() => {
    return () => {
      // Cleanup if needed
    };
  }, []);

  React.useEffect(() => {
    const handleGlobalTouchStart = (e) => {
      if (isRunning) {
        e.preventDefault();
        handleStop();
      }
    };

    if (isRunning) {
      document.addEventListener('touchstart', handleGlobalTouchStart);
    }

    return () => {
      document.removeEventListener('touchstart', handleGlobalTouchStart);
    };
  }, [isRunning, handleStop]);

  React.useEffect(() => {
    if (isRunning) {
      document.body.classList.add('timer-running');
    } else {
      document.body.classList.remove('timer-running');
    }
    
    return () => {
      document.body.classList.remove('timer-running');
    };
  }, [isRunning]);

  return (
    <div className="timer-layout">
      <div className="timer">
        {children}
        <div 
          className={`timer-pad ${isRunning ? 'running' : ''} ${pressStartTime ? 'ready' : ''}`}
          onTouchStart={(e) => {
            if (!isRunning) {
              handlePressStart(e);
            }
          }}
          onTouchEnd={(e) => {
            if (!isRunning) {
              handlePressEnd(e);
            }
          }}
          onTouchCancel={(e) => {
            if (!isRunning) {
              handlePressEnd(e);
            }
          }}
          onMouseDown={handlePressStart}
          onMouseUp={handlePressEnd}
          onMouseLeave={handlePressEnd}
          style={{
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none'
          }}
        >
          {isRunning ? 
            'Tap anywhere to stop' : 
            pressStartTime ? 
              'Ready...' : 
              `${(elapsedTime / 1000).toFixed(3)}s`
          }
        </div>
        {pendingSaves > 0 && (
          <p className="timer-save-status" role="status">Saving...</p>
        )}
        {saveFailure && (
          <p className="timer-save-error" role="alert">
            {`${saveFailure.algorithmId} (${(saveFailure.time / 1000).toFixed(2)}s) was not saved.`}
          </p>
        )}
      </div>
      <StatsContainer 
        algorithmId={algorithmId} 
        mode={mode} 
        newTime={savedEventId}
      />
    </div>
  );
};

export default Timer;
